import React, { useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { EmailOutlined, PriorityHigh } from '@mui/icons-material';
import { ActionItemKeys, ActionItemStatus, ProviderActivationEventName } from '@uplift/api';
import { providerScheduleViewTypeRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { L10nHTML, trackToDoCTA, useAllActionItems, useL10n } from '@uplift/hooks';
import { useProviderActivationEvents, useProviderTrainingsQuery } from '@uplift/queries';
import { ProviderMedicalRole, UserRole } from '@uplift/types';
import {
  FocusedElement, FocusedElementTooltip, LinkDashboardActionItem, ModalContent, ModalTitle,
} from '@uplift/ui-v2';

import { getAggregateGateStatus } from '../../onboarding-dashboard/components/todo-items/RequiredTrainings/helpers';
import { useDashboardStyles } from '../styles';
import {
  ChartsReviewOverdueToDo, ChartsReviewPendingToDo, isHighRisk, NewClientActionItem,
  NewPsychAppointment, NewPsychNote, PatientFilledFormActionItem, RequiredTrainingsToDo,
  SetUp2fa, ToDosList,
} from './components';
import { VerifyAccountEmail } from './components/verify-email.component';
import { SetupPayoutAccount } from './components/setup-payout-account.component';
import { UnsignedCollaborativeAgreements } from './components/sign-documents.component';
import { ExpiringCollaborativeAgreements } from './components/sign-expiring-documents.component';
import { ExpiredCollaborativeAgreements } from './components/sign-expired-documents.component';
import { PhysicianUnsignedCollaborativeAgreements } from './components/sign-physician-documents.component';
import { CollabAgreementRejected } from './components/collab-agreement-rejected.component';

const inviteClientsLink = providerScheduleViewTypeRoute.createLink({}, { createAppointment: 'invite' });

const displayPriorities = [
  ActionItemKeys.PROVIDER_SETUP_PAYOUT_ACCOUNT,
  ActionItemKeys.PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS,
  ActionItemKeys.PROVIDER_EXPIRING_COLLABORATIVE_AGREEMENTS,
  ActionItemKeys.PROVIDER_EXPIRED_COLLABORATIVE_AGREEMENTS,
  ActionItemKeys.PROVIDER_PHYSICIAN_UNSIGNED_COLLABORATIVE_AGREEMENT,
  ActionItemKeys.PROVIDER_CHARTS_REVIEWS_OVERDUE,
  ActionItemKeys.PROVIDER_CHARTS_REVIEWS_PENDING,
  ActionItemKeys.PROVIDER_INVITE_CLIENTS,
  ActionItemKeys.PROVIDER_SET_UP_2FA,
  ActionItemKeys.PROVIDER_TRAINING,
  ActionItemKeys.PROVIDER_COLLABORATIVE_AGREEMENT_REJECTED,
  ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS,
  ActionItemKeys.PROVIDER_VERIFY_ACCOUNT_EMAIL,
  ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK,
  ActionItemKeys.PROVIDER_NEW_CLIENTS,
  ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH,
  ActionItemKeys.PROVIDER_NEW_PSYCH_NOTE,
  ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT,
].reduce((acc, key, index) => {
  acc[key] = index + 1;
  return acc;
}, {} as Record<ActionItemKeys, number>);

export const ToDos: React.VFC = () => {
  const [ focusInviteClients, setFocusInviteClients ] = React.useState(false);
  const { classes } = useDashboardStyles();
  const { data: toDoActionItems, isLoading, dismiss } = useAllActionItems();
  const l10n = useL10n();
  const me = useCurrentuser();
  const { data: onboardingEvents } = useProviderActivationEvents(me.id);
  const { data: requiredTrainingsData } = useProviderTrainingsQuery(me?.id, { requiredOnly: true });

  useEffect(() => {
    if (onboardingEvents) {
      const hasCompletedOnboarding = onboardingEvents?.some(event => event.eventName === ProviderActivationEventName.confettiDisplayed);

      if (hasCompletedOnboarding) {
        setFocusInviteClients(true);
      }
    }
  }, [ onboardingEvents ]);

  useEffect(() => {
    if (toDoActionItems.providerInviteClients?.show && focusInviteClients) {
      setTimeout(() => {
        window.scrollTo({ top: 250, behavior: 'smooth' });
      }, 300);
    }
  }, [ focusInviteClients, toDoActionItems.providerInviteClients?.show ]);

  const addInviteClientsToDo = useMemo(() => {
    const handleDismiss = () => {
      dismiss({ key: ActionItemKeys.PROVIDER_INVITE_CLIENTS });
    };

    const handleClick = () => {
      dismiss({ key: ActionItemKeys.PROVIDER_INVITE_CLIENTS });
      trackToDoCTA({
        toDoType: ActionItemKeys.PROVIDER_INVITE_CLIENTS,
        userType: UserRole.provider
      });
    };

    if (!toDoActionItems.providerInviteClients?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_INVITE_CLIENTS],
      item: (
        <FocusedElement open={focusInviteClients}>
          <FocusedElementTooltip
            open={focusInviteClients}
            content={(
              <Box width="350px">
                <ModalTitle onClose={() => {
                  setFocusInviteClients(false);
                }}
                >{l10n.dashboard.actionItems.inviteClients.tooltipTitle}</ModalTitle>
                <ModalContent style={{ marginTop: '-2rem' }}>
                  <Typography style={{ paddingBottom: '0.5rem' }}>
                    {l10n.dashboard.actionItems.inviteClients.tooltipScheduleSessionsWithUpLift}
                  </Typography>
                  <L10nHTML variant="body1" color="textPrimary">
                    {l10n.dashboard.actionItems.inviteClients.tooltipInviteCurrentClient}
                  </L10nHTML>
                </ModalContent>
              </Box>
            )}
          >
            <Box display="flex">
              <LinkDashboardActionItem
                title={l10n.dashboard.actionItems.inviteClients.title}
                color="info"
                icon={<EmailOutlined />}
                link={inviteClientsLink}
                actionText="Invite Clients"
                dismiss={{
                  text: l10n.dashboard.actionItems.inviteClients.dismissText,
                  handleClick: handleDismiss
                }}
                onClick={handleClick}
              />
            </Box>
          </FocusedElementTooltip>
        </FocusedElement>
      )
    };
  }, [ toDoActionItems.providerInviteClients?.show, focusInviteClients, l10n.dashboard.actionItems, dismiss ]);

  const unsubmittedSessionsToDo = useMemo(() => {
    if (!toDoActionItems.providerUnsubmittedSessions?.unsubmittedSessionCount) {
      return null;
    }

    const unsubmittedCount = toDoActionItems.providerUnsubmittedSessions.unsubmittedSessionCount;

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS],
      item: (
        <LinkDashboardActionItem
          title={l10n.dashboard.actionItems.unsubmittedSessions.title(unsubmittedCount)}
          color="danger"
          icon={<PriorityHigh />}
          actionText={l10n.dashboard.actionItems.unsubmittedSessions.buttonText(unsubmittedCount)}
          link={ROUTING_CONFIG.notSubmittedSessions}
          onClick={() => trackToDoCTA({
            toDoType: ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS,
            userType: UserRole.provider
          })}
        />
      )
    };
  }, [ l10n.dashboard.actionItems.unsubmittedSessions, toDoActionItems.providerUnsubmittedSessions ]);

  const firstPsychApptToDos = useMemo(() => {
    if (!toDoActionItems.providerPatientScheduledPsych?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerPatientScheduledPsych?.clients)) {
      return null;
    }

    const sorted = [ ...toDoActionItems.providerPatientScheduledPsych.clients ]
      .sort((a, z) => a.nextTherapyAppointment.localeCompare(z.nextTherapyAppointment));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH],
      item: sorted.map(x => <NewPsychAppointment key={x.clientId} {...x} />)
    };
  }, [ toDoActionItems.providerPatientScheduledPsych ]);

  const newPsychNoteToDos = useMemo(() => {
    if (!toDoActionItems.providerNewPsychNote?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerNewPsychNote?.clients)) {
      return null;
    }

    const sorted = [ ...toDoActionItems.providerNewPsychNote.clients ]
      .sort((a, z) => a.nextAppointment.localeCompare(z.nextAppointment));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_NEW_PSYCH_NOTE],
      item: sorted.map(x => <NewPsychNote key={x.patientId} {...x} />)
    };
  }, [ toDoActionItems.providerNewPsychNote ]);

  const newClientsToDos = useMemo(() => {
    if (!toDoActionItems.providerNewClients?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerNewClients?.clients)) {
      return null;
    }

    const sortedNewClients = [ ...toDoActionItems.providerNewClients.clients ]
      .sort((a, z) => a.appointmentTime.localeCompare(z.appointmentTime));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_NEW_CLIENTS],
      item: sortedNewClients.map(x => (
        <NewClientActionItem
          appointmentId={x.appointmentId}
          appointmentTime={x.appointmentTime}
          firstName={x.firstName}
          lastName={x.lastName}
          fullName={x.fullName}
          userId={x.userId}
          key={x.userId}
          referral={x.referral}
          subsystem={x.subsystem}
          datetime={x.datetime}
        />
      ))
    };
  }, [ toDoActionItems.providerNewClients ]);

  const { highRisk, lowRisk } = useMemo(() => {
    if (!toDoActionItems.providerPatientFilledAssessment?.patients) {
      return { lowRisk: null, highRisk: null };
    }

    if (!Array.isArray(toDoActionItems.providerPatientFilledAssessment?.patients)) {
      return { lowRisk: null, highRisk: null };
    }

    const sortedClients = [ ...toDoActionItems.providerPatientFilledAssessment.patients ]
      .sort((a, z) => a.nextAppointmentAt.localeCompare(z.nextAppointmentAt));

    const lowRisk = {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT],
      item: sortedClients.filter(x => !isHighRisk(x)).map(x => <PatientFilledFormActionItem patient={x} key={x.patientId} />)
    };

    if (me.medicalRole === ProviderMedicalRole.psychiatrist) {
      return {
        lowRisk,
        highRisk: null
      };
    }

    const highRisk = {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK],
      item: sortedClients.filter(x => isHighRisk(x)).map(x => <PatientFilledFormActionItem patient={x} key={x.patientId} />)
    };

    return {
      highRisk,
      lowRisk
    };
  }, [ me.medicalRole, toDoActionItems.providerPatientFilledAssessment?.patients ]);

  const setUp2faToDo = useMemo(() => {
    // there's an issue with the camel casing. 2FA -> 2Fa instead of 2fa as this seems to be expecting
    if (!toDoActionItems['providerSetUp2Fa']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_SET_UP_2FA],
      item: (
        <SetUp2fa userId={me?.id} />
      )
    };
  }, [ me?.id, toDoActionItems ]);

  const setupPayoutAccountToDo = useMemo(() => {
    if (!toDoActionItems['providerSetupPayoutAccount']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_SETUP_PAYOUT_ACCOUNT],
      item: (
        <SetupPayoutAccount />
      )
    };
  }, [ toDoActionItems ]);

  const signDocuments = useMemo(() => {
    if (!toDoActionItems['providerUnsignedCollaborativeAgreements']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS],
      item: (
        <UnsignedCollaborativeAgreements stateIds={toDoActionItems['providerUnsignedCollaborativeAgreements'].stateIds} />
      )
    };
  }, [ toDoActionItems ]);

  const signExpiringDocuments = useMemo(() => {
    if (!toDoActionItems['providerExpiringCollaborativeAgreements']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_EXPIRING_COLLABORATIVE_AGREEMENTS],
      item: (
        <ExpiringCollaborativeAgreements stateIds={toDoActionItems['providerExpiringCollaborativeAgreements'].stateIds} />
      )
    };
  }, [ toDoActionItems ]);

  const signExpiredDocuments = useMemo(() => {
    if (!toDoActionItems['providerExpiredCollaborativeAgreements']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_EXPIRED_COLLABORATIVE_AGREEMENTS],
      item: (
        <ExpiredCollaborativeAgreements stateIds={toDoActionItems['providerExpiredCollaborativeAgreements'].stateIds} />
      )
    };
  }, [ toDoActionItems ]);

  const signPhysicianDocuments = useMemo(() => {
    if (!toDoActionItems['providerPhysicianUnsignedCollaborativeAgreement']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_PHYSICIAN_UNSIGNED_COLLABORATIVE_AGREEMENT],
      item: (
        <PhysicianUnsignedCollaborativeAgreements stateIds={toDoActionItems['providerPhysicianUnsignedCollaborativeAgreement'].stateIds} />
      )
    };
  }, [ toDoActionItems ]);

  const verifyAccountEmail = useMemo(() => {
    if (!toDoActionItems.providerVerifyAccountEmail?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_SET_UP_2FA],
      item: (
        <VerifyAccountEmail />
      )
    };
  }, [ toDoActionItems ]);

  const collabAgreementRejectedToDos = useMemo(() => {
    const rejectedAgreements = toDoActionItems.providerCollaborativeAgreementRejected?.rejectedAgreements;

    if (!Array.isArray(rejectedAgreements) || rejectedAgreements.length === 0) {
      return null;
    }

    const handleDismiss = (agreementId: string) => {
      dismiss({ key: ActionItemKeys.PROVIDER_COLLABORATIVE_AGREEMENT_REJECTED, id: agreementId });
    };

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_COLLABORATIVE_AGREEMENT_REJECTED],
      item: rejectedAgreements.map(agreement => (
        <CollabAgreementRejected key={agreement.agreementId} agreement={agreement} onDismiss={handleDismiss} />
      ))
    };
  }, [ toDoActionItems.providerCollaborativeAgreementRejected, dismiss ]);

  const chartsReviewPendingToDo = useMemo(() => {
    const chartsReviewsCount = toDoActionItems.providerChartsReviewsPending?.chartsReviewsCount;

    if (!chartsReviewsCount) return null;

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_CHARTS_REVIEWS_PENDING],
      item: (
        <ChartsReviewPendingToDo
          chartsReviewsCount={chartsReviewsCount}
        />
      ),
    };
  }, [ toDoActionItems.providerChartsReviewsPending ]);

  const chartsReviewOverdueToDo = useMemo(() => {
    const chartsReviewsCount = toDoActionItems.providerChartsReviewsOverdue?.chartsReviewsCount;

    if (!chartsReviewsCount) return null;

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_CHARTS_REVIEWS_OVERDUE],
      item: (
        <ChartsReviewOverdueToDo
          chartsReviewsCount={chartsReviewsCount}
        />
      ),
    };
  }, [ toDoActionItems.providerChartsReviewsOverdue ]);

  const requiredTrainingsToDo = useMemo(() => {
    if (!requiredTrainingsData?.trainings.length) {
      return null;
    }

    if (getAggregateGateStatus(requiredTrainingsData.trainings) === ActionItemStatus.done) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_TRAINING],
      item: (
        <RequiredTrainingsToDo
          trainings={requiredTrainingsData.trainings}
          lastSyncedAt={requiredTrainingsData.lastSyncedAt}
        />
      )
    };
  }, [ requiredTrainingsData ]);

  const toDos = useMemo(() => {
    const items = [
      signDocuments,
      signExpiringDocuments,
      signExpiredDocuments,
      signPhysicianDocuments,
      setupPayoutAccountToDo,
      addInviteClientsToDo,
      unsubmittedSessionsToDo,
      firstPsychApptToDos,
      newPsychNoteToDos,
      highRisk,
      newClientsToDos,
      lowRisk,
      setUp2faToDo,
      requiredTrainingsToDo,
      verifyAccountEmail,
      collabAgreementRejectedToDos,
      chartsReviewPendingToDo,
      chartsReviewOverdueToDo,
    ]
      .filter(x => x)
      .sort((a, b) => a.order - b.order)
      .flatMap(x => x.item);

    return items;
  }, [
    addInviteClientsToDo,
    unsubmittedSessionsToDo,
    firstPsychApptToDos,
    newPsychNoteToDos,
    highRisk,
    newClientsToDos,
    lowRisk,
    setUp2faToDo,
    requiredTrainingsToDo,
    verifyAccountEmail,
    setupPayoutAccountToDo,
    signDocuments,
    signExpiringDocuments,
    signExpiredDocuments,
    signPhysicianDocuments,
    collabAgreementRejectedToDos,
    chartsReviewPendingToDo,
    chartsReviewOverdueToDo,
  ]);

  const title = (
    <Typography variant="h3">
      {l10n.dashboard.toDo.numberOfItems(toDos.length)}&nbsp;
      <span className={classes.secondary}>{l10n.dashboard.toDo.toDo}</span>
    </Typography>
  );

  return (
    <div style={{ marginBottom: 300 }}>
      <Box mb={3} ml={{ xs: 2, sm: 0 }}>
        {title}
      </Box>
      <ToDosList toDos={toDos} loading={isLoading} />
    </div>
  );
};
